"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect } from "react";
import { LangSwitch } from "@/components/lang-switch";
import { Button, Spinner } from "@/components/ui";
import { AuthGate, useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import type { Key } from "@/i18n/id";

type NavItem = { href: string; key: Key; adminOnly?: boolean };

const NAV: NavItem[] = [
  { href: "/", key: "nav.dashboard", adminOnly: true },
  { href: "/factory", key: "nav.factory" },
  { href: "/units", key: "nav.units" },
  { href: "/types", key: "nav.types" },
  { href: "/handover", key: "nav.handover", adminOnly: true },
  { href: "/growth", key: "nav.growth", adminOnly: true },
  { href: "/audit", key: "nav.audit", adminOnly: true },
  { href: "/settings", key: "nav.settings", adminOnly: true },
];

function Shell({ children }: { children: React.ReactNode }) {
  const t = useT();
  const { user, isAdmin, logout } = useAuth();
  const pathname = usePathname();
  const router = useRouter();

  // A user with a temporary password must set their own before doing anything else.
  useEffect(() => {
    if (user.must_change_password && pathname !== "/account") router.replace("/account");
    // Workers have no dashboard: send them to the factory screen.
    if (!isAdmin && pathname === "/") router.replace("/factory");
  }, [user, isAdmin, pathname, router]);

  const active = (href: string) => (href === "/" ? pathname === "/" : pathname === href || pathname.startsWith(`${href}/`));

  return (
    <>
      <header className="shell-head">
        <div className="shell-bar">
          <Link href={isAdmin ? "/" : "/factory"} className="brand">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/netro-icon-white.png" alt="" width={35} height={36} className="brand-logo" />
            <span>Netro Production System</span>
          </Link>
          <LangSwitch />
          <div className="shell-user">
            <Link href="/account" style={{ color: "inherit" }}>
              {user.full_name}
            </Link>
            <span className="badge" style={{ background: "rgb(255 255 255 / 22%)", color: "inherit" }}>
              {t(`role.${user.role}`)}
            </span>
            <Button size="sm" onClick={logout} style={{ background: "transparent", color: "inherit", borderColor: "rgb(255 255 255 / 45%)" }}>
              {t("nav.logout")}
            </Button>
          </div>
        </div>
        <nav className="nav" aria-label={t("nav.label")}>
          {NAV.filter((n) => isAdmin || !n.adminOnly).map((n) => (
            <Link key={n.href} href={n.href} aria-current={active(n.href) ? "page" : undefined}>
              {t(n.key)}
            </Link>
          ))}
        </nav>
      </header>
      <main className="main">{children}</main>
    </>
  );
}

export default function AppLayout({ children }: { children: React.ReactNode }) {
  return (
    <AuthGate
      fallback={
        <div className="main">
          <Spinner />
        </div>
      }
    >
      <Shell>{children}</Shell>
    </AuthGate>
  );
}
